// TASK-67 PR-D — frontend refactor: drop teams.player1/2_id/_customer_id
// and replace with team_members membership reads. The four legacy slot
// helpers (`getTeamPlayerIdentity`, `getTeamPlayerDisplay`,
// `resolveSlotDisplay`, `TeamPlayersShape`) are kept for the few admin
// fixtures + read-only display-only callers that haven't been migrated
// yet (e.g. CSV-export loops can construct a TeamPlayersShape from a
// team_members iteration before passing to `resolveSlotDisplay`). Once
// PR-E drops the four columns, these helpers become inert wrappers
// around the team_members iteration — keeping them avoids churning the
// 7+ callers in a single PR.
//
// History:
//   - UPPDRAG-12.1 introduced `teams.captain_id` as the explicit captain
//     pointer. Helpers `getTeamCaptainId` + `isCaptain` were added to
//     resolve "who is the captain" with a player1_id fallback.
//   - UPPDRAG-62 PR-A made captain_id + player1_id nullable and added
//     customer-side pointers (captain_customer_id, player1_customer_id,
//     player2_customer_id), introducing dual-identity helpers
//     getTeamCaptainIdentity + getTeamCaptainDisplay.
//   - TASK-66 PR-A REVERSES UPPDRAG-12.1 + 62 captain semantics: the
//     captain concept is dropped entirely. Both players are equal. The
//     four captain helpers (getTeamCaptainId, isCaptain,
//     getTeamCaptainIdentity, getTeamCaptainDisplay) are removed because
//     they no longer make sense — there is no captain to identify or
//     gate on. Schema columns `teams.captain_id` + `captain_customer_id`
//     remain for now (dropped in TASK-66 PR-E); production code stops
//     reading or writing them as of TASK-66 PR-A.
//   - TASK-67 PR-D — frontend refactor stops reading teams.player1/2_id
//     entirely. New helper `pickFirstTwoPrimaries` collapses a
//     team_members embed to the canonical "first 2 primaries by joined_at"
//     pair that callsites used to read as player1/2.
//
// Player slot helpers (`getTeamPlayerIdentity`, `getTeamPlayerDisplay`,
// `resolveSlotDisplay`) survive — they target player1/player2 only and
// have no captain semantics. `TeamCaptainShape` is renamed to
// `TeamPlayersShape` and trimmed to player1/player2 fields only.
//
// Roster split + validate helpers (`splitRoster`, `validateRosterSize`).
// TASK-66 PR-E: enum value `'captain'` dropped — the captain-tolerant
// arms (read 'captain' OR 'primary') from PR-D are now dead code and
// removed. TypeScript would complain anyway since 'captain' is no
// longer in the TeamMemberRole union.

import type { Tables } from "@/integrations/supabase/types";

/**
 * Minimal shape required to read a team's player slots. Accepts either
 * a full Tables<"teams"> row or any object with the relevant fields.
 * Keeps the helper usable in test fixtures + JSON-shape responses
 * without requiring the full row.
 *
 * TASK-66 PR-A: dropped `captain_id` + `captain_customer_id` fields.
 * Renamed from `TeamCaptainShape`. Production callers access this via
 * `getTeamPlayerIdentity` / `getTeamPlayerDisplay` (player1/player2).
 *
 * TASK-67 PR-D: production callers no longer construct this from
 * direct teams.* row reads — they construct it from the first two
 * primaries returned by `pickFirstTwoPrimaries`. The shape itself is
 * preserved so the slot resolvers below keep working unchanged for
 * read-only display flows.
 */
export interface TeamPlayersShape {
  player1_id?: string | null;
  player2_id?: string | null;
  /** UPPDRAG-62 PR-A: customer-side pointer for unregistered player1. */
  player1_customer_id?: string | null;
  /** UPPDRAG-62 PR-A: customer-side pointer for unregistered player2. */
  player2_customer_id?: string | null;
}

// TASK-66 PR-D: removed deprecated `TeamCaptainShape` alias. Use `TeamPlayersShape`.

// ─── UPPDRAG-62 PR-A: SlotIdentity + display resolvers ─────────────────
//
// The captain + each player slot can now hold EITHER a registered user
// (auth.users.id) OR an unregistered customer (customers.id), enforced
// mutually-exclusive by the teams_*_identity_chk CHECK constraints.
//
// TASK-66 PR-A: captain helpers (getTeamCaptainIdentity +
// getTeamCaptainDisplay) removed. Player slot helpers
// (getTeamPlayerIdentity + getTeamPlayerDisplay) preserved — they don't
// touch captain.

export interface SlotIdentity {
  /** Registered identity. Non-null when slot holds an auth.users id. */
  userId: string | null;
  /** Unregistered identity. Non-null when slot holds a customers.id. */
  customerId: string | null;
  /** Convenience: userId !== null. */
  isRegistered: boolean;
  /** Convenience: at least one of userId/customerId is set. */
  isPresent: boolean;
}

// TASK-66 PR-A: `getTeamCaptainIdentity` REMOVED. The 4-deep fallback
// chain (captain_id → captain_customer_id → player1_id →
// player1_customer_id) had its entire purpose tied to the captain
// concept. Use `getTeamPlayerIdentity(team, "player1")` for the
// equivalent of "first player" lookups.

/**
 * Returns the identity of the requested player slot (`player1` or `player2`).
 * Returns userId/customerId pair — exactly one is set per the
 * teams_*_identity_chk CHECK constraints (slot 1 mandatory, slot 2 may
 * be both NULL for solo registrations).
 */
export function getTeamPlayerIdentity(
  team: TeamPlayersShape,
  slot: "player1" | "player2",
): SlotIdentity {
  const userId = slot === "player1" ? team.player1_id ?? null : team.player2_id ?? null;
  const customerId =
    slot === "player1" ? team.player1_customer_id ?? null : team.player2_customer_id ?? null;
  return {
    userId,
    customerId,
    isRegistered: userId !== null,
    isPresent: userId !== null || customerId !== null,
  };
}

/**
 * Display name + identity for a slot. Consumer provides resolved profile
 * (when registered) and/or customer (when unregistered or as fallback).
 *
 * Fallback chain (registered):
 *   profile.full_name → customer.full_name → customer.email → unknownLabel
 * Fallback chain (unregistered):
 *   customer.full_name → customer.email → unknownLabel
 *
 * Returns the same shape regardless of identity type, so renderers can
 * spread `display.identity` into PlayerNameButton-style components
 * without branching.
 */
export interface SlotDisplay {
  name: string;
  identity: SlotIdentity;
}

export function resolveSlotDisplay(
  identity: SlotIdentity,
  profile: { full_name: string | null } | null | undefined,
  customer: { full_name: string | null; email: string | null } | null | undefined,
  unknownLabel: string,
): SlotDisplay {
  if (identity.isRegistered) {
    const profileName = profile?.full_name?.trim();
    const customerName = customer?.full_name?.trim();
    const name = profileName || customerName || customer?.email || unknownLabel;
    return { name, identity };
  }
  if (identity.customerId) {
    const customerName = customer?.full_name?.trim();
    const name = customerName || customer?.email || unknownLabel;
    return { name, identity };
  }
  return { name: unknownLabel, identity };
}

// TASK-66 PR-A: `getTeamCaptainDisplay` REMOVED. The convenience
// wrapper for captain → SlotDisplay no longer makes sense. Use
// `getTeamPlayerDisplay(team, "player1", data, unknownLabel)` for the
// equivalent first-player display.

/**
 * Convenience: resolve player1/player2 display from a team + embedded data.
 */
export function getTeamPlayerDisplay(
  team: TeamPlayersShape,
  slot: "player1" | "player2",
  data: {
    profile?: { full_name: string | null } | null;
    customer?: { full_name: string | null; email: string | null } | null;
  },
  unknownLabel: string,
): SlotDisplay {
  return resolveSlotDisplay(
    getTeamPlayerIdentity(team, slot),
    data.profile,
    data.customer,
    unknownLabel,
  );
}

/**
 * Type alias for code that wants the full DB row.
 */
export type TeamRow = Tables<"teams">;

// ─── PR-12.2: team_members n-player helpers ────────────────────────

import type { Database } from "@/integrations/supabase/types";

export type TeamMemberRow = Tables<"team_members">;
export type TeamMemberRole = Database["public"]["Enums"]["team_member_role"];

/**
 * Roster split helper: takes a team's full member list and returns
 * primaries + reserves separately. Useful for UI that renders
 * sections (e.g. "Spelare — A. B. / C. D. / Reserver — G. H.").
 *
 * TASK-66 PR-E: enum collapsed to ('primary', 'reserve'). Pre-PR-E
 * 21 'captain' rows were backfilled to 'primary' in the migration.
 *
 * Invariants enforced by the DB schema (PR-12.2 migration):
 *   - One row per (team, user) — UNIQUE constraint.
 */
export function splitRoster<M extends { role: TeamMemberRole }>(members: M[]): {
  primaries: M[];
  reserves: M[];
} {
  const primaries: M[] = [];
  const reserves: M[] = [];
  for (const m of members) {
    if (m.role === "primary") primaries.push(m);
    else if (m.role === "reserve") reserves.push(m);
  }
  return { primaries, reserves };
}

// ─── TASK-67 PR-A: joined_at-sorted helpers ────────────────────────────
//
// `getTeamPrimaryPlayers` + `getTeamReservePlayers` return role-filtered
// member lists ordered by `joined_at` ASC. The deterministic ordering
// matches the server-side semantic used in the post-PR-A
// `sync_league_match_to_match_results` trigger function (which picks
// "first 2 primaries by joined_at" — see migration 20260504080000).
//
// Use these instead of `splitRoster` when consumers need a stable order
// (e.g. picking "the first 2 primaries" to display, or pairing players
// in match generation). `splitRoster` retains its O(n) single-pass
// shape for UI sections that don't care about order beyond grouping.

/**
 * Returns the team's primary roster members sorted by joined_at ASC
 * (oldest first). The order is deterministic and matches the server-side
 * trigger logic that picks "first 2 primaries" for match_results.
 */
export function getTeamPrimaryPlayers<
  M extends { role: TeamMemberRole; joined_at: string },
>(members: M[]): M[] {
  return members
    .filter((m) => m.role === "primary")
    .sort((a, b) => a.joined_at.localeCompare(b.joined_at));
}

/**
 * Returns the team's reserve roster members sorted by joined_at ASC.
 * Same ordering invariant as `getTeamPrimaryPlayers`.
 */
export function getTeamReservePlayers<
  M extends { role: TeamMemberRole; joined_at: string },
>(members: M[]): M[] {
  return members
    .filter((m) => m.role === "reserve")
    .sort((a, b) => a.joined_at.localeCompare(b.joined_at));
}

/**
 * Validation predicate: does the roster meet the league's min/max
 * player range? Returns null on valid; returns a reason string for
 * the toast/error UI on invalid. Excludes reserves from the count
 * (they're bench, not playing roster).
 *
 * Used by PR-12.3+ admin UI when accepting roster changes.
 */
export function validateRosterSize(
  members: TeamMemberRow[],
  league: { min_players_per_team: number; max_players_per_team: number },
): string | null {
  // TASK-66 PR-E: was `m.role === "captain" || m.role === "primary"`
  // in PR-D for backward-compat with pre-PR-E rows. Enum dropped
  // 'captain' so the check is now just 'primary'.
  const playingCount = members.filter((m) => m.role === "primary").length;
  if (playingCount < league.min_players_per_team) {
    return `För få spelare: ${playingCount} av minst ${league.min_players_per_team}`;
  }
  if (playingCount > league.max_players_per_team) {
    return `För många spelare: ${playingCount} av högst ${league.max_players_per_team}`;
  }
  return null;
}

// ─── UPPDRAG-60: getTeamMemberDisplay ──────────────────────────────────
//
// Resolves a team_members row to a display name + registration status,
// handling the new dual-identity model where a member can be either a
// registered user (via user_id → profiles) OR an unregistered customer
// (via customer_id → customers). The existing AdminTeams query embeds
// both `profile.full_name` and `profile.customers[0]` (UPPDRAG-27 chain),
// so this helper just wraps the existing fallback chain plus a separate
// branch for the customer_id path.
//
// Fallback chain (registered):
//   1. profile.full_name (if truthy + non-empty)
//   2. profile.customers[0].full_name (UPPDRAG-27 — matchi-imported
//      profiles often ship with empty full_name)
//   3. profile.customers[0].email
//   4. unknownLabel
// Fallback chain (unregistered, customer_id only):
//   1. customer.full_name
//   2. customer.email
//   3. unknownLabel

export interface TeamMemberDisplayInput {
  user_id: string | null;
  customer_id?: string | null;
  profile: {
    full_name: string | null;
    customers?: { id: string; full_name: string | null; email: string | null }[];
  } | null;
  /** UPPDRAG-60: present for unregistered members (no user_id, only customer_id). */
  customer?: { id: string; full_name: string | null; email: string | null } | null;
}

export interface TeamMemberDisplay {
  name: string;
  isRegistered: boolean;
  /** Convenience pointer for PlayerNameButton — already-org-scoped customer id. */
  customerId: string | null;
  userId: string | null;
}

export function getTeamMemberDisplay(
  member: TeamMemberDisplayInput,
  unknownLabel: string,
): TeamMemberDisplay {
  // Registered path: user_id non-null → resolve via profile + embedded
  // customer fallback (UPPDRAG-27 chain).
  if (member.user_id) {
    const profileName = member.profile?.full_name?.trim();
    const embedded = member.profile?.customers?.[0];
    const customerName = embedded?.full_name?.trim();
    const name = profileName || customerName || embedded?.email || unknownLabel;
    return {
      name,
      isRegistered: true,
      customerId: embedded?.id ?? null,
      userId: member.user_id,
    };
  }
  // Unregistered path: only customer_id is set. Resolve via the
  // dedicated `customer` field the parent attaches per row.
  if (member.customer_id && member.customer) {
    const cName = member.customer.full_name?.trim();
    const name = cName || member.customer.email || unknownLabel;
    return {
      name,
      isRegistered: false,
      customerId: member.customer.id,
      userId: null,
    };
  }
  // Defensive: neither identity present (should be impossible due to the
  // CHECK constraint, but the consumer types it as nullable).
  return { name: unknownLabel, isRegistered: false, customerId: null, userId: null };
}

// ─── TASK-67 PR-D: pickFirstTwoPrimaries — embed-swap collapser ────────
//
// Most pre-PR-D consumer queries embedded
//   player1:profiles!teams_player1_id_fkey(full_name)
//   player2:profiles!teams_player2_id_fkey(full_name)
// and rendered the pair as "{p1} / {p2}". After PR-D the embed becomes
//   members:team_members(user_id, role,
//     profile:profiles!team_members_user_id_fkey(full_name))
// and the renderer needs to filter to role='primary' + sort by joined_at +
// pick the first two — exactly the trigger-side semantics PR-A's
// `sync_league_match_to_match_results` uses for match_results pairs.
//
// `pickFirstTwoPrimaries` does that collapse in one place so callers can
// pass `pickFirstTwoPrimaries(team.members)` and get back a stable
// [first, second] tuple — second may be null for solo registrations or
// when the team only has one primary.
//
// Accepts any member shape with `role` + `joined_at` (no profile/customer
// embed required), so it works with both the registered (user_id) and
// unregistered (customer_id) flavour.

export interface PickedPrimary<M> {
  member: M | null;
}

export function pickFirstTwoPrimaries<
  M extends { role: TeamMemberRole; joined_at: string },
>(members: M[] | null | undefined): [M | null, M | null] {
  if (!members || members.length === 0) return [null, null];
  const primaries = getTeamPrimaryPlayers(members);
  return [primaries[0] ?? null, primaries[1] ?? null];
}

/**
 * Convenience: extract the first 2 primaries' display names from a
 * team_members embed row that includes a profile (or customer) blob,
 * matching the PostgREST shape:
 *   members:team_members(user_id, customer_id, role, joined_at,
 *     profile:profiles!team_members_user_id_fkey(full_name))
 *
 * Returns [name1, name2] strings — uses the unknownLabel fallback when
 * a slot is missing or has no resolvable name. Used by team-label
 * renderers (e.g. "Andersson / Eriksson") to replace the legacy
 * player1/player2 split.
 */
export function pickFirstTwoPrimaryNames<
  M extends {
    role: TeamMemberRole;
    joined_at: string;
    profile?: { full_name: string | null } | null;
    customer?: { full_name: string | null; email: string | null } | null;
  },
>(members: M[] | null | undefined, unknownLabel: string): [string, string] {
  const [p1, p2] = pickFirstTwoPrimaries(members);
  function nameOf(m: M | null): string {
    if (!m) return unknownLabel;
    const profileName = m.profile?.full_name?.trim();
    const customerName = m.customer?.full_name?.trim();
    return profileName || customerName || m.customer?.email || unknownLabel;
  }
  return [nameOf(p1), nameOf(p2)];
}

// ─── TASK-67 PR-D Feature 1: auto-generated team_name on team create ────
//
// When the user leaves the "Lagnamn" field empty at create time, derive a
// stable team_name from the primary players' full_names joined with "/".
// Stored verbatim in `teams.team_name` — NOT recomputed on render — so the
// label is stable even if a player later leaves the roster (the user can
// edit the name explicitly via the TeamCard pencil-edit affordance — see
// Feature 2).
//
// Examples:
//   2 primaries:  "Henry Edvardsen/Anders Rönquist"
//   3 primaries:  "Henry Edvardsen/Anders Rönquist/Sarah Larsson"
//   1 primary:    "Henry Edvardsen"
//   0 primaries:  ""  (caller should treat as no auto-name available)
//
// Resolution chain per primary (mirrors getTeamMemberDisplay /
// pickFirstTwoPrimaryNames): profile.full_name → customer.full_name →
// customer.email → drop-from-list.
//
// Reserves are NOT included in the auto-name — only ordinarie spelare
// (role='primary') contribute. Order is by joined_at ASC (same as
// pickFirstTwoPrimaries) so the auto-name is stable on re-render.

export function generateAutoTeamName<
  M extends {
    role: TeamMemberRole;
    joined_at: string;
    profile?: { full_name: string | null } | null;
    customer?: { full_name: string | null; email: string | null } | null;
  },
>(members: M[] | null | undefined): string {
  if (!members || members.length === 0) return "";
  const primaries = getTeamPrimaryPlayers(members);
  if (primaries.length === 0) return "";
  const names = primaries
    .map((m) => {
      const profileName = m.profile?.full_name?.trim();
      const customerName = m.customer?.full_name?.trim();
      return profileName || customerName || m.customer?.email?.trim() || "";
    })
    .filter((n) => n.length > 0);
  return names.join("/");
}
